import { GridOptions } from 'ag-grid-community';
import { DatePipe, formatDate } from '@angular/common';
import { Component, Inject, Input, LOCALE_ID, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ContactStateCellRendererComponent } from '../../contact-list/contact-list-details/contact-state-cell-renderer/contact-state-cell-renderer.component';
import { LinkedContactsDetailsModel } from '../linked-contacts.model';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cv-linked-contacts-details',
    templateUrl: './linked-contacts-details.component.html',
    styleUrls: ['./linked-contacts-details.component.scss']
})
export class LinkedContactsDetailsComponent implements OnInit {
    @ViewChild('linkedContactsDetailsGrid') agGrid: AgGridAngular;
    @Input() data: LinkedContactsDetailsModel;
    @Input() moduleKey: string;
    @Input() moduleManager: ModuleManager;

    // Ag-grid configuration:
    public gridOptions: GridOptions = null;

    private stateSaveTimer: any;
    private filterValue = "";
    private restoreInProgress = false;
    private tableState: any;
    private gridState: any = null; // This object holds previous state for ag-grid, and saves it to

    constructor(
        private activeModal: NgbActiveModal,
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        @Inject(LOCALE_ID) private locale: string,
        private datePipe: DatePipe
    ) {
        this.setupGrid();
    }

    ngOnInit(): void {
    }

    public closeModal() {
        this.activeModal.close();
    }

    public get CVMomentDateTimeWithSecondsFormat(): string {
        return window['CVAngularDateTimeWithSecondsFormat'];
    }

    public formatSecondsToTime(valueInSeconds): string {
        let formatted = "";
        const hours = Math.floor(valueInSeconds / 3600);
        valueInSeconds %= 3600;
        const minutes = Math.floor(valueInSeconds / 60);
        valueInSeconds %= 60;
        const seconds = valueInSeconds;

        formatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

        return formatted;
    }

    // Called by AngularJS when the search bar's value has changed.
    public applyFilter() {
        if (this.agGrid?.api) { // notify ag-grid that the filter changed
            // this.agGrid?.api.onFilterChanged(); // Used with the External Filter.
            this.filterValue = this.filterValue.trim();
            this.agGrid?.api.setGridOption('quickFilterText', this.filterValue);
        }
    }

    /**
     * Called when the clear button on the filter box is clicked.  Clears the filter
     */
    public clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    }

    /**
     * Retrieves the table state and saves it
     */
    updateTableState() {
        if (!this.agGrid?.api || this.restoreInProgress)
            return;

        // generate state from grid
        const tableState = this.agGridHelperService.getGridState(this.agGrid?.api);
        if (tableState != null) {
            this.tableState = tableState;
        }

        // tell module manager to save the state
        this.moduleManager.saveState('tableState');
    };

    private _getStateColor(stateName: string) {
        const stateColors: { [color: string]: string } = {
            green: "#3D9649",
            purple: "#7C52BF",
            yellow: "#DC9C24",
            red: "#BD2026",
            blue: "#0078AC",
            orange: "#F24813",
            cyan: "#00BBBD",
            brown: "#A47748",
            pink: "#E74181",
            black: "#000",
        };

        switch(stateName.toLowerCase()) {
            case 'prequeue':
            case 'routing':
                return stateColors['blue'];
            case 'inqueue':
                return stateColors['red'];
            case 'active':
                return stateColors['green'];
            case 'transfer':
                return stateColors['purple'];
            case 'abandoned':
                return stateColors['brown'];
            case 'endcontact':
                return stateColors['purple'];
            default:
                return stateColors['blue'];
        }
    }

    /**
     * Sets up the grid options.  Should be called after localizations are loaded, because the column headers are localized.
     */
    private setupGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({ // Please use AgGridHelperService.  This way your grid options will inherit logic all modules should have.  Properties you define in this object will replace the ones from the factory.
            components: {
                ContactStateCellRenderer: ContactStateCellRendererComponent
            },
            columnDefs: [
                {
                    field: "MasterContactId", // This is the key name from object representing this row's data.  rowData is an array of objects.
                    colId: "MasterContactId", // Specifying a column ID is optional, but if you change the fields, saved state won't match later.
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Master_Contact_ID", "Master Contact ID"),
                    filter: "agTextColumnFilter",
                },
                {
                    field: "ContactId",
                    colId: "ContactId",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Contact_ID", "Contact ID"),
                    filter: "agTextColumnFilter",
                },
                {
                    field: "LinkedContactId",
                    colId: "LinkedContactId",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Linked_Contact_ID", "Linked Contact ID"),
                    filter: "agTextColumnFilter",
                    valueGetter: params => params.data.LinkedContactId.length > 0 ? params.data.LinkedContactId : " - ",
                },
                {
                    field: "BU",
                    colId: "BU",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "BU", "BU"),
                    filter: "agTextColumnFilter",
                },
                {
                    field: "StartDate",
                    colId: "StartDate",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Start_Date", "Start Date"),
                    filter: "agTextColumnFilter",
                    valueFormatter: params => this.datePipe.transform(params.data.StartDate, this.CVMomentDateTimeWithSecondsFormat)
                },
                {
                    field: "StateName",
                    colId: "State",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "State", "State"),
                    filter: "agTextColumnFilter",
                    cellRenderer: "ContactStateCellRenderer",
                    cellRendererParams: {
                        showTotals: false,
                        customColorGetter: this._getStateColor
                    },
                },
                {
                    field: "DurationInSeconds",
                    colId: "DurationInSeconds",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Duration", "Duration"),
                    filter: "agTextColumnFilter",
                    valueFormatter: params => this.formatSecondsToTime(params.data.DurationInSeconds),
                    cellClass: 'ag-right-aligned-cell'
                },
                {
                    field: "Skill",
                    colId: "Skill",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Skill", "Skill"),
                    filter: "agTextColumnFilter",
                },
                {
                    field: "Agent",
                    colId: "Agent",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "Agent", "Agent"),
                    filter: "agTextColumnFilter",
                    valueGetter: params => params.data.Agent.length > 0 ? params.data.Agent : " - ",
                },
                {
                    field: "DNIS",
                    colId: "DNIS",
                    headerName: this.cvLocalizationService.getCachedText("Linked_Contacts", "DNIS", "DNIS"),
                    filter: "agTextColumnFilter",
                },
            ],
            onGridReady: params => {
                if (this.data) {
                    params.api.setGridOption('rowData', this.data.SubContacts);
                }
                if (this.gridState)
                    this.agGridHelperService.loadGridState(this.agGrid?.api, this.gridState);
                this.applyFilter();
                this.agGrid?.api.sizeColumnsToFit();
            },

            // This function defines which field from the row data is the "primary key".  It's used for delta updates between different setRowData() calls.
            // This "ID" property is the same as the above columnDef.  Usually it'll be a group id.  If there is no "primary key" for the data you're trying to display.
            getRowId: ({ data: rowData }) => `${rowData.ContactId}_${rowData.StateIndex}`,

            // The filter can also be set with the setQuickFilter() api call on the grid.
            // isExternalFilterPresent: () => this.filterValue.length > 0, // External Filter refers to our search bar.  This function returns true when $ctrl.filterValue has content, false when otherwise.  False causes ag-grid to show all row values.
            // doesExternalFilterPass: node => this.agGridHelperService.doesExternalFilterPass(node, this.filterValue), // This function actually uses the value of the search bar, and it compares it to the data of the row ag-grid is passing in (node).  Use agGridHelperFactory's unless you have good reason.
            onSortChanged: event => this.queueStateSave(),
            onColumnMoved: event => this.queueStateSave(),
            onColumnPinned: event => this.queueStateSave(),
            onColumnVisible: event => {
                event.api.sizeColumnsToFit();
                this.queueStateSave();
            },
        });
    }

    /**
     * Saves the grid's state save, debounced to 500ms.
     */
    private queueStateSave() {
        // Cancel previous save attempt, since it hasn't been 500ms, yet.
        if (this.stateSaveTimer) {
            clearTimeout(this.stateSaveTimer);
            this.stateSaveTimer = null;
        }

        // Create a timer to save the state 500ms from now.
        this.stateSaveTimer = window.setTimeout(() => {
            this.stateSaveTimer = null;
            if (this.agGrid?.api)
                this.gridState = this.agGridHelperService.getGridState(this.agGrid?.api);
            this.moduleManager.saveState(); // This will save all moduleManager.addState definitions
        }, 500/*ms*/);
    }
}
